import useScrollReveal from "@/hooks/useScrollReveal";

interface AIFact {
  label: string;
  value: string;
}

interface AIAnswerBlockProps {
  tagline: string;
  title: string;
  description: string;
  facts: AIFact[];
  whoItIsFor: string[];
  servicesList: string[];
  image: string;
}

export default function AIAnswerBlock({
  tagline,
  title,
  description,
  facts,
  whoItIsFor,
  servicesList,
  image,
}: AIAnswerBlockProps) {
  const leftRef = useScrollReveal<HTMLDivElement>();
  const rightRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="bg-cream py-32 px-6 lg:px-14 overflow-hidden relative">
      {/* Decorative background text */}
      <div className="absolute right-0 bottom-0 font-display text-[200px] font-bold leading-none text-stone-200 select-none pointer-events-none opacity-60 translate-x-16 translate-y-10">
        GDA
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* AI Answer label — schema-like indicator */}
        <div className="flex items-center gap-3 mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white border border-stone-200 rounded-full shadow-sm">
            <div className="w-4 h-4 flex items-center justify-center text-gold">
              <i className="ri-robot-line text-xs"></i>
            </div>
            <span className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">AI Answer Block</span>
            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">
          {/* Left: Structured content */}
          <div ref={leftRef} className="sr-hidden">
            <div className="flex items-center gap-4 mb-5">
              <span className="gold-rule" />
              <p className="text-gold font-sans text-[11px] font-bold uppercase tracking-[0.22em]">{tagline}</p>
            </div>

            <h2 className="font-display text-4xl md:text-5xl text-charcoal font-bold mb-6 leading-tight">
              {title}
            </h2>

            <p className="text-stone-500 font-sans text-base leading-relaxed mb-10 pl-5 border-l-2 border-gold">
              {description}
            </p>

            {/* Structured facts — AI-extractable grid */}
            <div className="mb-10">
              <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-4">Business Information</p>
              <div className="grid grid-cols-2 gap-2">
                {facts.map((fact, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border border-stone-200 hover:border-gold/40 transition-colors">
                    <p className="text-stone-400 font-sans text-[10px] uppercase tracking-[0.15em] mb-1.5">{fact.label}</p>
                    <p className="text-charcoal font-sans text-sm font-bold">{fact.value}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Two columns: services + who it's for */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-4">Services Offered</p>
                <ul className="flex flex-col gap-2.5">
                  {servicesList.map((s, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700 font-sans">
                      <div className="w-5 h-5 flex items-center justify-center text-gold mt-0.5 flex-shrink-0">
                        <i className="ri-checkbox-circle-fill text-sm"></i>
                      </div>
                      <strong className="font-semibold">{s}</strong>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <p className="font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-stone-400 mb-4">Ideal Clients</p>
                <ul className="flex flex-col gap-2.5">
                  {whoItIsFor.map((w, i) => (
                    <li key={i} className="flex items-start gap-2.5 text-sm text-stone-700 font-sans">
                      <div className="w-5 h-5 flex items-center justify-center text-gold mt-0.5 flex-shrink-0">
                        <i className="ri-user-star-fill text-sm"></i>
                      </div>
                      {w}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Right: Image + floating badge */}
          <div ref={rightRef} className="sr-hidden sr-delay-2 relative order-first lg:order-last">
            <div className="relative w-full overflow-hidden rounded-lg" style={{ height: "600px" }}>
              <img
                src={image}
                alt={title + " – Glen Davis and Associates Photography Bremerton WA"}
                title={title}
                className="w-full h-full object-cover object-top"
              />
              {/* Overlay gradient at bottom */}
              <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 via-transparent to-transparent" />
            </div>

            {/* Floating investment badge */}
            <div className="absolute -bottom-6 -left-5 hidden lg:block bg-charcoal text-white px-7 py-5 rounded-lg border border-stone-700 shadow-xl">
              <p className="font-display text-gold text-3xl font-bold">$450</p>
              <p className="text-stone-400 font-sans text-[10px] uppercase tracking-widest mt-1">Starting Investment</p>
            </div>

            {/* Gold accent bar top-left */}
            <div className="absolute top-0 right-0 w-1.5 h-28 bg-gold rounded-bl-full" />

            {/* Schema-style info panel overlaid on image bottom */}
            <div className="absolute bottom-0 left-0 right-0 p-6 hidden lg:block">
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-gold/40" />
                <span className="text-stone-400 font-sans text-[9px] uppercase tracking-widest whitespace-nowrap">Automotive Photographer · Bremerton WA · Since 2024</span>
                <div className="flex-1 h-px bg-gold/40" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
